import { OrderSuccessIcon } from "@assets/icon";
import React, { useEffect, useMemo, useState } from "react";
import {
    BlockContent,
    CommandVariable,
    MessageButton,
    OrderInfoPayload,
    ProductInfo,
    TextBlockPayload,
} from "../../../../types/chat-message";
import { safePostMessage } from "../../../../utils";
import { getButtonStyle } from "@utils/buttonUtils";
import "./OrderCard.scss";
import { useTranslation } from "react-i18next";
import type { ToastMessage } from '@hooks/useChat';

interface OrderCardProps {
  blockContents: BlockContent[];
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
  onThinkingStop?: () => void;
  onScrollToBottom?: () => void;
  isLastMessage?: boolean;
  chatMessageTime?: string;
  isFromHistory?: boolean; // Flag to indicate if message is from history
  setToastMessage?: (toast: NonNullable<ToastMessage>) => void;
}

// Helper function to check if button should be hidden
const checkIsHidden = (commandVariables?: CommandVariable[]): boolean => {
  if (!commandVariables) return false;
  const hiddenVar = commandVariables.find((v) => v.name === "hidden_message");
  if (!hiddenVar) return false;
  return hiddenVar.value === "true";
};

// Helper function to check if a value is a valid number
const isValidNumber = (value: string | undefined): boolean => {
  if (!value) return false;
  const num = parseFloat(value.replace(/[^0-9.-]/g, ""));
  return !isNaN(num) && isFinite(num);
};

// Helper function to parse number safely
const parseNumber = (value: string | undefined): number => {
  if (!value) return NaN;
  return parseFloat(value.replace(/[^0-9.-]/g, ""));
};

// Helper function to format number with 2 decimal places
const formatNumber = (value: number): string => {
  return value.toLocaleString("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

// Process products with validation
const processProducts = (products: ProductInfo[]): ProductInfo[] => {
  if (products.length === 0) return products;

  return products.map((product) => {
    const quantityValid = isValidNumber(product.quantity);
    const priceValid = isValidNumber(product.price);
    const totalValid = isValidNumber(product.totalAmount);

    let processedQuantity = product.quantity;
    let processedPrice = product.price;
    let processedTotal = product.totalAmount;

    // Case: If totalAmount is invalid
    if (!totalValid && priceValid && quantityValid) {
      // Calculate total from price * quantity
      const qty = parseNumber(product.quantity);
      const price = parseNumber(product.price);
      processedTotal = formatNumber(qty * price);
    }
    // Case: If totalAmount is still invalid, try calculating from price and quantity
    else if (!totalValid && priceValid && !quantityValid) {
      // Calculate quantity from total / price (if we had total)
      // But if total is also invalid, we can't calculate
      // So we'll mark quantity as 1
      processedQuantity = "1";
      const qty = parseNumber(processedQuantity);
      const price = parseNumber(product.price);
      processedTotal = formatNumber(qty * price);
    }
    // Case: If quantity is invalid but price and total are valid
    else if (!quantityValid && priceValid && totalValid) {
      // Calculate quantity from total / price
      const total = parseNumber(product.totalAmount);
      const price = parseNumber(product.price);
      const qty = total / price;
      processedQuantity = formatNumber(Math.round(qty * 100) / 100);
    }
    // Case: If quantity is invalid and price is also invalid
    else if (!quantityValid && !priceValid) {
      // Default quantity to 1
      processedQuantity = "1";
    }

    // If price is invalid, we can't calculate anything, keep as is

    return {
      ...product,
      quantity: processedQuantity,
      price: processedPrice,
      totalAmount: processedTotal,
    };
  });
};

const OrderCard: React.FC<OrderCardProps> = ({
  blockContents,
  onButtonClick,
  onThinkingStop,
  onScrollToBottom,
  isLastMessage,
  chatMessageTime,
  isFromHistory,
  setToastMessage,
}) => {
  const { t } = useTranslation();

  // Scroll to bottom on mount
  useEffect(() => {
    onScrollToBottom?.();
  }, [onScrollToBottom]);

  const [selectedInvoice, setSelectedInvoice] = useState(false);

  // 1. Sort Blocks Dynamically by Order
  const sortedBlocks = [...blockContents].sort((a, b) => {
    const orderA = (a.payload as any).order ?? 0;
    const orderB = (b.payload as any).order ?? 0;
    return orderA - orderB;
  });

  const orderInfoBlock = blockContents.find((b) => b.type === "ORDER_INFO");
  const rawProducts: ProductInfo[] =
    (orderInfoBlock?.payload as OrderInfoPayload)?.products || [];

  // Process products with validation
  const products = useMemo(() => processProducts(rawProducts), [rawProducts]);

  // Find button block (also contains paymentMethod)
  const buttonBlock = blockContents.find(
    (b) =>
      b.type === "TEXT" &&
      (b.payload as TextBlockPayload).buttons &&
      (b.payload as TextBlockPayload).buttons!.length > 0,
  );
  const buttons = (buttonBlock?.payload as TextBlockPayload)?.buttons || [];
  const paymentMethod = (buttonBlock?.payload as TextBlockPayload)
    ?.paymentMethod;

  // Process Buttons
  const paymentButtons = buttons.filter((btn: MessageButton) =>
    btn.commandVariables?.some(
      (v: CommandVariable) => v.name === "payment_method",
    ),
  );

  const actionButtons = buttons.filter(
    (btn: MessageButton) =>
      !btn.commandVariables?.some(
        (v: CommandVariable) => v.name === "payment_method",
      ),
  );

  const defaultPaymentIndex = useMemo(() => {
    const idx = paymentButtons.findIndex((btn: MessageButton) =>
      btn.commandVariables?.some(
        (v: CommandVariable) => v.name === "is_choose" && v.value === "true",
      ),
    );
    return idx !== -1 ? idx : 0;
  }, [paymentButtons]);

  const [isActionTaken, setIsActionTaken] = useState(false);
  const [selectedPaymentIndex, setSelectedPaymentIndex] =
    useState(defaultPaymentIndex);

  useEffect(() => {
    setSelectedPaymentIndex(defaultPaymentIndex);
  }, [defaultPaymentIndex]);

  const activePaymentBtn =
    paymentButtons[selectedPaymentIndex] || paymentButtons[0];

  // Countdown logic
  // Get countdown value from active payment button
  const countdownVar = activePaymentBtn?.commandVariables?.find(
    (v: CommandVariable) => v.name === "count_down",
  );
  const initialCountdown = countdownVar
    ? parseInt(countdownVar.value, 10)
    : null;

  const [countdown, setCountdown] = useState<number | null>(null);

  // Initialize/Manage countdown
  useEffect(() => {
    if (initialCountdown !== null && !isActionTaken && !isFromHistory) {
      if (isLastMessage) {
        const msgTime = chatMessageTime
          ? new Date(chatMessageTime).getTime()
          : Date.now();
        const now = Date.now();
        const secondsPassed = Math.max(0, Math.floor((now - msgTime) / 1000));

        const remaining = initialCountdown - secondsPassed;
        setCountdown(Math.max(0, remaining));
      } else {
        setCountdown(null);
      }
    } else {
      setCountdown(null);
    }
  }, [isLastMessage, chatMessageTime, initialCountdown, isActionTaken, isFromHistory]);

  // Broadcast countdown active state to window so EmbedBox can intercept agent switch
  useEffect(() => {
    const isActive = countdown !== null && countdown > 0 && !isActionTaken;
    window.dispatchEvent(
      new CustomEvent("order-countdown-change", { detail: { isActive } }),
    );
    return () => {
      // When unmounted, signal countdown is gone
      window.dispatchEvent(
        new CustomEvent("order-countdown-change", {
          detail: { isActive: false },
        }),
      );
    };
  }, [countdown, isActionTaken]);

  // Huỷ countdown khi đóng chatbot
  useEffect(() => {
    const handleChatbotClose = () => {
      setCountdown(null);
      setIsActionTaken(true);
    };
    window.addEventListener('chatbot-close', handleChatbotClose);
    return () => window.removeEventListener('chatbot-close', handleChatbotClose);
  }, []);

  // Timer effect
  useEffect(() => {
    if (countdown === null) return;

    if (countdown <= 0) {
      if (!isActionTaken && isLastMessage && initialCountdown !== null) {
        const enterButton = buttons.find(
          (btn: MessageButton) => btn.shortCut === "ENTER",
        );
        if (enterButton) {
          handleButtonClick(enterButton.name, enterButton.id, enterButton);
        }
      }
      setCountdown(null);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => (prev !== null && prev > 1 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown, isActionTaken, isLastMessage, buttons, initialCountdown]);

  const handleButtonClick = (name: string, id: number, btn: MessageButton) => {
    if (isActionTaken || !isLastMessage) return;

    if (!btn.isHasPermission) {
      setToastMessage?.({
        text: t('common.noPermissions'),
        type: 'error',
      });
      return;
    }
    
    // Check for deeplink - only emit if button has no shortCut
    const deeplinkVar = btn.commandVariables?.find(
      (v) => v.name === "deeplink_web",
    );
    const isDeeplinkButton = deeplinkVar && !btn.shortCut;

    // Check if button should be hidden
    const isHidden = checkIsHidden(btn.commandVariables);

    // For deeplink buttons, only emit event without sending to socket
    if (isDeeplinkButton) {
      // Disable button and stop countdown
      setIsActionTaken(true);
      setCountdown(null);

      // Emit deeplink event
      setTimeout(() => {
        const rawUrl = deeplinkVar?.value || '';
        let routePath = rawUrl;
        try {
          const parsed = new URL(rawUrl);
          routePath = parsed.pathname + parsed.search + parsed.hash;
        } catch { /* rawUrl is already a relative path */ }
        console.log('[Deeplink] NAVIGATE_ROUTE ->', routePath);
        safePostMessage({ type: "NAVIGATE_ROUTE", payload: routePath });
        // Stop thinking after emitting event
        onThinkingStop?.();
      }, 100);
      return;
    }

    // For buttons with shortCut, disable immediately
    setIsActionTaken(true);
    setCountdown(null);

    if (onButtonClick) {
      onButtonClick(name, id, isHidden);
    }
  };

  const handlePaymentToggle = (index: number) => {
    if (
      isActionTaken ||
      !isLastMessage ||
      isFromHistory ||
      index === selectedPaymentIndex
    )
      return;

    const btn = paymentButtons[index];
    setSelectedPaymentIndex(index);

    // Check if button should be hidden
    const isHidden = checkIsHidden(btn.commandVariables);

    // Emit button click
    setIsActionTaken(true);
    setCountdown(null);
    if (onButtonClick) {
      onButtonClick(btn.name, btn.id, isHidden);
    }
  };

  const isButtonDisabled = isActionTaken || !isLastMessage || isFromHistory;

  return (
    <div className="order-card-container">
      {sortedBlocks.map((block, idx) => {
        if (block.type === "TEXT") {
          const textPayload = block.payload as TextBlockPayload;
          if (textPayload.buttons && textPayload.buttons.length > 0) {
            return (
              <React.Fragment key={idx}>
                {paymentMethod && (
                  <div className="order-info-row">
                    <span className="label">
                      {textPayload.text || "Thanh toán"}
                    </span>
                    <span className="value">{paymentMethod}</span>
                  </div>
                )}

                {paymentButtons.length >= 2 ? (
                  <div className="payment-toggle-container">
                    <span className="label">
                      {textPayload.text || "Thanh toán"}
                    </span>
                    <div className={`payment-toggle ${isButtonDisabled ? "disabled" : ""}`}>
                      {paymentButtons.map((btn, btnIdx) => (
                        <button
                          key={btn.id || btnIdx}
                          className={`payment-toggle-item ${selectedPaymentIndex === btnIdx ? "active" : ""} ${isButtonDisabled ? "disabled" : ""}`}
                          onClick={() => handlePaymentToggle(btnIdx)}
                        >
                          {btn.name}
                        </button>
                      ))}
                      {!isButtonDisabled && (
                        <div
                          className="payment-toggle-slider"
                          style={{
                            transform: `translateX(${selectedPaymentIndex * 100}%)`,
                          }}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  paymentButtons.length === 1 && (
                    <div className="payment-row">
                      <span className="label">
                        {textPayload.text || "Thanh toán"}
                      </span>
                      <span className="value">{paymentButtons[0].name}</span>
                    </div>
                  )
                )}

                {countdown !== null && countdown > 0 && !isActionTaken && (
                  <div className="countdown-text">
                    Đơn hàng sẽ tự động hoàn thành sau{" "}
                    <span className="seconds">{countdown}s</span>
                  </div>
                )}

                <div className="divider"></div>
                <div className="action-buttons">
                  {[...actionButtons]
                    .sort((a, b) => (a.intent === "cancel_order" ? -1 : 1))
                    .map((btn, btnIdx) => {
                      const isBusinessNoShortcut =
                        btn.type === "BUSINESS" && !btn.shortCut && btn.content;
                      const isCancel = btn.intent === "cancel_order";
                      const isDisableInvoiceDetail = btn.intent === "invoice_detail";
                      const isBtnDisabled = isButtonDisabled || (isDisableInvoiceDetail && selectedInvoice);

                      // Base fallback style khi không có color_button_web
                      let fallbackStyle: any = isBusinessNoShortcut
                        ? {
                            backgroundColor: "var(--colour-primary-light-xxl)",
                            color: "var(--colour-primary-shade-md)",
                            borderColor: "var(--colour-primary-light-xxl)",
                            borderStyle: "solid",
                            borderWidth: "1px",
                          }
                        : {
                            backgroundColor: "#ffffff",
                            color: "#52525b",
                            borderColor: "#d4d4d8",
                            borderStyle: "solid",
                            borderWidth: "1px",
                          };

                      const colorStyle = getButtonStyle(btn, isBtnDisabled);
                      let finalStyle: any = Object.keys(colorStyle).length > 0
                        ? colorStyle
                        : fallbackStyle;

                      if (isBtnDisabled && !Object.keys(colorStyle).length && isDisableInvoiceDetail && selectedInvoice) {
                        finalStyle = { ...finalStyle, opacity: 0.5 };
                      }

                      return (
                        <button
                          key={btn.id || btnIdx}
                          onClick={() => {
                            if (isDisableInvoiceDetail)
                              setSelectedInvoice(true);
                            handleButtonClick(btn.name, btn.id, btn);
                          }}
                          disabled={isButtonDisabled}
                          className={`action-btn ${isCancel ? "btn-cancel" : "btn-primary"} ${isBusinessNoShortcut ? "btn-full-width" : ""} ${isButtonDisabled ? "opacity-50 cursor-not-allowed disabled" : ""} ${isDisableInvoiceDetail && selectedInvoice ? "disabled" : ""}`}
                          style={finalStyle}
                        >
                          {btn.name}
                        </button>
                      );
                    })}
                </div>
              </React.Fragment>
            );
          }

          if (textPayload.isBold) {
            return (
              <div key={idx} className="order-section-title">
                {textPayload.text}
                {textPayload.status && (
                  <span className="order-status-badge">
                    {textPayload.status}
                  </span>
                )}
              </div>
            );
          }

          // Otherwise it's a status/header text
          return (
            <>
              <div key={idx} className="order-header-status">
                {textPayload.text?.toLowerCase().includes("thành công") ? (
                  <>
                    <div className="success-icon">
                      <OrderSuccessIcon />
                    </div>
                    <span>{textPayload.text.replace("!", "")}!</span>
                  </>
                ) : (
                  <>
                    <div className="success-icon">
                      <OrderSuccessIcon />
                    </div>
                    <span>{textPayload.text}</span>
                  </>
                )}
              </div>
              <div key={idx} className="divider"></div>
            </>
            
          );
        }

        // if (block.type === "DIVIDER") {
        //   return <div key={idx} className="divider"></div>;
        // }

        if (block.type === "ORDER_INFO") {
          return (
            <React.Fragment key={idx}>
              {products.length > 0 ? (
                <div className="order-info-row">
                  <span className="label">
                    {products
                      .map(
                        (p) =>
                          `${p.quantity === "1" ? "" : p.quantity + " "}${p.name}`,
                      )
                      .join(", ")}
                  </span>
                  <span className="value">
                    {products[0].totalAmount || "0"}
                  </span>
                </div>
              ) : products.length === 0 ? (
                <div className="order-info-row">
                  <span className="label">Tổng tiền</span>
                  <span className="value">{"0"}</span>
                </div>
              ) : null}
            </React.Fragment>
          );
        }

        return null;
      })}
    </div>
  );
};

export default OrderCard;
